import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import walletService from '../../services/walletService';

const COIN_OPTIONS = [
  { value: 'USDT', label: 'USDT — Tether', networks: ['ETH', 'BSC', 'TRX', 'SOL'] },
  { value: 'BTC',  label: 'BTC — Bitcoin',  networks: ['BTC'] },
  { value: 'ETH',  label: 'ETH — Ethereum', networks: ['ETH'] },
  { value: 'BNB',  label: 'BNB — BNB Chain', networks: ['BSC'] },
  { value: 'SOL',  label: 'SOL — Solana',   networks: ['SOL'] },
  { value: 'XRP',  label: 'XRP — Ripple',   networks: ['XRP'] },
];

const NETWORK_LABELS = {
  ETH: 'Ethereum (ERC-20)', BSC: 'BNB Smart Chain (BEP-20)',
  TRX: 'TRON (TRC-20)', SOL: 'Solana', BTC: 'Bitcoin',
  XRP: 'Ripple', DOGE: 'Dogecoin', LTC: 'Litecoin',
};

const MIN_AMOUNTS = {
  USDT: 10, BTC: 0.0001, ETH: 0.01, BNB: 0.1, SOL: 0.1, XRP: 1
};

const styles = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, zIndex: 9999, backdropFilter: 'blur(4px)'
  },
  modal: {
    background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16, width: '100%', maxWidth: 460,
    maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 25px 50px rgba(0,0,0,0.5)'
  },
  header: {
    padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
  },
  body: { padding: 24 },
  label: { color: '#94a3b8', fontSize: 12, fontWeight: 600,
    display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: {
    width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '12px 16px', color: '#e2e8f0',
    fontSize: 14, boxSizing: 'border-box', outline: 'none',
    transition: 'border-color 0.2s'
  },
  select: {
    width: '100%', background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10, padding: '12px 16px', color: '#e2e8f0',
    fontSize: 14, boxSizing: 'border-box', cursor: 'pointer', appearance: 'none'
  },
  fieldGroup: { marginBottom: 16 },
};

export default function WithdrawModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    asset: 'USDT', amount: '', chain: 'ETH', to_address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setFormData({ asset: 'USDT', amount: '', chain: 'ETH', to_address: '' });
      setError('');
      setSuccess(false);
    }
  }, [isOpen]);

  const selectedCoin = COIN_OPTIONS.find(c => c.value === formData.asset);
  const minAmount = MIN_AMOUNTS[formData.asset] || 1;

  const handleCoinChange = (e) => {
    const coin = e.target.value;
    const coinObj = COIN_OPTIONS.find(c => c.value === coin);
    setFormData(f => ({
      ...f,
      asset: coin,
      chain: coinObj?.networks?.[0] || 'ETH'
    }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.to_address) return setError(t('auth.fill_all_fields'));
    if (!formData.amount || parseFloat(formData.amount) <= 0) return setError(t('trading.invalid_amount'));
    if (parseFloat(formData.amount) < minAmount) return setError(`${t('wallet.min')}: ${minAmount} ${formData.asset}`);

    try {
      setLoading(true);
      await walletService.requestWithdrawal({
        asset: formData.asset,
        amount: parseFloat(formData.amount),
        chain: formData.chain,
        to_address: formData.to_address,
        wallet_type: 'spot'
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || t('trading.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ asset: 'USDT', amount: '', chain: 'ETH', to_address: '' });
    setError('');
    setSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 18, fontWeight: 700 }}>
              🏧 {t('wallet.withdraw_request')}
            </h2>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
              {t('wallet.withdraw_process_time')}
            </div>
          </div>
          <button onClick={handleClose} style={{
            background: 'none', border: 'none', color: '#64748b',
            cursor: 'pointer', fontSize: 20, padding: 4
          }}>✕</button>
        </div>

        <div style={styles.body}>
          {success ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
              <h3 style={{ color: '#34d399', margin: '0 0 8px' }}>{t('wallet.submitted')}</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, margin: '0 0 24px' }}>
                {t('wallet.submitted_desc')}
              </p>
              <button onClick={handleClose} style={{
                background: '#6366f1', border: 'none', borderRadius: 10,
                padding: '12px 24px', color: '#fff', cursor: 'pointer',
                fontWeight: 600, fontSize: 14
              }}>{t('wallet.close')}</button>
            </div>
          ) : (
            <>
              {/* Coin */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t('wallet.coin')}</label>
                <select style={styles.select} value={formData.asset} onChange={handleCoinChange}>
                  {COIN_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* Réseau */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t('wallet.withdraw_network')}</label>
                <select
                  style={styles.select}
                  value={formData.chain}
                  onChange={e => setFormData(f => ({ ...f, chain: e.target.value }))}
                >
                  {(selectedCoin?.networks || []).map(net => (
                    <option key={net} value={net}>{net} — {NETWORK_LABELS[net] || net}</option>
                  ))}
                </select>
              </div>

              {/* Montant */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t('wallet.amount_withdraw')}</label>
                <input
                  type="number"
                  step="0.00000001"
                  min={minAmount}
                  placeholder={`${t('wallet.min')}: ${minAmount} ${formData.asset}`}
                  value={formData.amount}
                  onChange={e => setFormData(f => ({ ...f, amount: e.target.value }))}
                  style={styles.input}
                />
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>
                  {t('wallet.min')}: {minAmount} {formData.asset}
                </div>
              </div>

              {/* Adresse destination */}
              <div style={styles.fieldGroup}>
                <label style={styles.label}>{t('wallet.dest_address')}</label>
                <input
                  type="text"
                  placeholder={
                    formData.chain === 'BTC' ? 'bc1q...' :
                    formData.chain === 'TRX' ? 'T...' :
                    '0x...'
                  }
                  value={formData.to_address}
                  onChange={e => setFormData(f => ({ ...f, to_address: e.target.value }))}
                  style={{ ...styles.input, fontFamily: 'monospace', fontSize: 13 }}
                />
              </div>

              {/* Avertissement */}
              <div style={{
                background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.2)',
                borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: '#fbbf24'
              }}>
                ⚠️ {t('wallet.withdraw_warning')}
              </div>

              {/* Erreur */}
              {error && (
                <div style={{
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                  borderRadius: 10, padding: 12, marginBottom: 16,
                  color: '#fca5a5', fontSize: 13
                }}>⚠️ {error}</div>
              )}

              {/* Bouton */}
              <button
                onClick={handleSubmit}
                disabled={loading || !formData.amount || !formData.to_address}
                style={{
                  width: '100%', background: loading ? '#374151' : '#ef4444',
                  border: 'none', borderRadius: 10, padding: '14px',
                  color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: 15, transition: 'background 0.2s',
                  opacity: (!formData.amount || !formData.to_address) ? 0.5 : 1
                }}
              >
                {loading ? t('wallet.processing') : `🏧 ${t('wallet.action_withdraw')} ${formData.amount || '0'} ${formData.asset}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}